import { Button, Checkbox, FormControlLabel, Grid, TextField, Tooltip } from '@mui/material';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import ContactSelector from '../ContactSelector/ContactSelector';
import { cargoInputSelector, resetCargoInput, setCargoInputExpand, setCharterer, setSelectedCargoId, setCargoType, setDwtMin, setDwtMax, setLoadPort, setDisport, setLay, setCan, setRemarks, setOrderDetail, setTriggerFocusChartererInput, setCargoInput, setTriggerSave, setFixed } from './reducers/CargoInputReducer';
import CargoTypeSelector from '../CargoTypeSelector/CargoTypeSelector';
import CargoTypeModel from '../CargoTypeSelector/models/CargoTypeModel';
import { cargoTypeSelectorSelector } from '../CargoTypeSelector/reducers/CargoTypeSelectorReducer';
import DwtSelector from '../Tonnage/components/DwtSelector';
import PortSelector from '../PortSelector/PortSelector';
import { portSelectorSelector } from '../PortSelector/reducers/PortSelectorReducer';
import PortModel from '../PortSelector/models/PortModel';
import StartEndDatepicker from '../StartEndDatepicker/StartEndDatepicker';
import CargoApi from './api/CargoApi';
import BaseResponse from '../../app/BaseResponse';
import CargoModel from './models/CargoModel';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import ContactNameModel from '../ContactSelector/models/ContactNameModel';
import { contactSelectorSelector, setContactSearchResults } from '../ContactSelector/reducers/ContactSelectorReducer';
import Swal from 'sweetalert2';
import { CargoInputModel } from './models/CargoInputModel';
import { resetCargoSearch, setCharterersForInput, setTriggerCargoSearch, setTriggerMatchingAreaModel } from './reducers/CargoSearchReducer';
import { MatchingType } from '../MatchingPreview/MatchingConstants';
import TriggerMatchingAreaModel from '../MatchingPreview/models/TriggerMatchingAreaModel';

const CargoInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const cargoInputViewData = useSelector(cargoInputSelector);
    let cargoTypeSelectorData = useSelector(cargoTypeSelectorSelector);
    let portSelectorData = useSelector(portSelectorSelector);
    const contactSelectorData = useSelector(contactSelectorSelector);

    useEffect(() => {
        if (cargoInputViewData.triggerSave > 0) {
            save(false);
            dispatch(setTriggerSave(0));
        }
    }, [cargoInputViewData.triggerSave]);

    useEffect(() => {
        if (cargoInputViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [cargoInputViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetCargoInput());
        dispatch(setSelectedCargoId(""));
        dispatch(setTriggerFocusChartererInput((new Date()).getTime()));
    }

    useEffect(() => {
        if (cargoInputViewData.selectedCargoId.length > 0) {
            selectCargoById(cargoInputViewData.selectedCargoId);
        }
    }, [cargoInputViewData.selectedCargoId])

    const selectCargoById = (id: string) => {
        dispatch(showLoading(true));
        CargoApi.GetById({
            id: id
        }).then((response: BaseResponse<CargoModel>) => {
            if (response.success) {
                let cargoInput = Utility.CloneObject(cargoInputViewData.cargoInput) as CargoInputModel;

                cargoInput.id = response.data.id;
                cargoInput.charterer = response.data.charterer ? response.data.charterer : "";
                cargoInput.orderDetail = response.data.orderDetail ? response.data.orderDetail : "";
                cargoInput.cargoType = response.data.cargoType;
                cargoInput.dwtMin = response.data.dwtMin ? response.data.dwtMin : null;
                cargoInput.dwtMax = response.data.dwtMax ? response.data.dwtMax : null;
                cargoInput.loadPort = response.data.loadPort;
                cargoInput.disport = response.data.disport;

                cargoInput.lay = Utility.DateFormat.format(new Date(response.data.lay));
                cargoInput.can = Utility.DateFormat.format(new Date(response.data.can));
                cargoInput.remarks = response.data.remarks ? response.data.remarks : "";
                cargoInput.fixed = response.data.fixed;

                setContactForContactSelector(response.data);

                dispatch(setCargoInput(cargoInput));

                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    };

    const setContactForContactSelector = (data: CargoModel) => {
        let searchResults = Utility.CloneObject(contactSelectorData.searchResults) as ContactNameModel[];

        searchResults.push({
            id: data.charterer,
            companyName: data.chartererCompanyName
        } as ContactNameModel);

        searchResults = _.uniqBy(searchResults, (item: ContactNameModel) => {
            return item.id;
        });
        dispatch(setContactSearchResults(searchResults));
    }

    const getCargoTypeById = (cargoTypeId: number) => {
        let selectedCargoType = _.find(cargoTypeSelectorData.cargoTypes, (cargo) => {
            return cargo.id === cargoTypeId;
        });

        if (selectedCargoType) {
            return selectedCargoType;
        }

        return null;
    }

    const getPortById = (portId: number) => {
        let selectedPort = _.find(portSelectorData.ports, (port) => {
            return port.id === portId;
        });

        if (selectedPort) {
            return selectedPort;
        }

        return null;
    }

    const save = (triggerM1: boolean) => {
        try {
            validate();

            dispatch(showLoading(true));

            CargoApi.Save(cargoInputViewData.cargoInput)
                .then((response: BaseResponse<CargoModel>) => {
                    if (response.success) {
                        if (cargoInputViewData.cargoInput.id.length === 0) {
                            Utility.Alert.Success("Cargo added successful");
                        } else {
                            Utility.Alert.Success("Cargo updated successful");
                        }

                        if (triggerM1) {
                            dispatch(setTriggerMatchingAreaModel({
                                triggerMatchingArea: new Date().getTime(),
                                matchingType: MatchingType.MatchingArea1,
                                model: response.data
                            } as TriggerMatchingAreaModel<CargoModel>))
                        }

                        dispatch(showLoading(false));

                        refreshInput();
                    } else {
                        if (response.message === "Need Confirm") {
                            Swal.fire({
                                title: 'The order you are trying to input may be existed. Do you wish to add anyway?',
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#3085d6',
                                cancelButtonColor: '#d33',
                                confirmButtonText: 'Yes',
                                cancelButtonText: 'No'
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    CargoApi.Save(cargoInputViewData.cargoInput)
                                        .then((response: BaseResponse<CargoModel>) => {
                                            if (response.success) {
                                                if (cargoInputViewData.cargoInput.id.length === 0) {
                                                    Utility.Alert.Success("Cargo added successful");
                                                } else {
                                                    Utility.Alert.Success("Cargo updated successful");
                                                }

                                                if (triggerM1) {
                                                    dispatch(setTriggerMatchingAreaModel({
                                                        triggerMatchingArea: new Date().getTime(),
                                                        matchingType: MatchingType.MatchingArea1,
                                                        model: response.data
                                                    } as TriggerMatchingAreaModel<CargoModel>))
                                                }

                                                dispatch(showLoading(false));

                                                refreshInput();
                                            }
                                        })
                                        .catch((error) => {
                                            dispatch(showLoading(false));
                                            Utility.Alert.Error(error);
                                        });
                                } else {
                                    dispatch(showLoading(false));
                                }
                            })
                        } else {
                            dispatch(showLoading(false));
                            Utility.Alert.Error(response.message);
                        }
                    }
                })
                .catch((error) => {
                    dispatch(showLoading(false));
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            dispatch(showLoading(false));
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (cargoInputViewData.cargoInput.charterer.length === 0) {
            throw "Account name is required";
        }

        if (cargoInputViewData.cargoInput.cargoType === 0) {
            throw "Cargo type is required";
        }

        if (!cargoInputViewData.cargoInput.dwtMin || cargoInputViewData.cargoInput.dwtMin === 0) {
            throw "Dwt min is required";
        }

        if (!cargoInputViewData.cargoInput.dwtMax || cargoInputViewData.cargoInput.dwtMax === 0) {
            throw "Dwt max is required";
        }

        if (cargoInputViewData.cargoInput.loadPort === 0) {
            throw "Loadport is required";
        }

        if (cargoInputViewData.cargoInput.disport === 0) {
            throw "Disport is required";
        }

        if (cargoInputViewData.cargoInput.lay.length === 0) {
            throw "Lay is required";
        }

        if (cargoInputViewData.cargoInput.can.length === 0) {
            throw "Can is required";
        }

        if (new Date(cargoInputViewData.cargoInput.lay).getTime() > new Date(cargoInputViewData.cargoInput.can).getTime()) {
            throw "Lay must before Can";
        }
    }

    const parseFullOrder = () => {
        if (!cargoInputViewData.cargoInput.orderDetail || cargoInputViewData.cargoInput.orderDetail.length == 0) {
            return;
        }

        dispatch(showLoading(true));

        CargoApi.ParseFullOrder({
            fullOrder: cargoInputViewData.cargoInput.orderDetail
        }).then((response) => {
            if (response.success) {
                if (response.data) {
                    if (response.data.charterer) {
                        dispatch(setCharterer(response.data.charterer));
                    }

                    if (response.data.cargoType) {
                        dispatch(setCargoType(response.data.cargoType));
                    }

                    if (response.data.dwtMin) {
                        dispatch(setDwtMin(response.data.dwtMin));
                    }

                    if (response.data.dwtMax) {
                        dispatch(setDwtMax(response.data.dwtMax));
                    }

                    if (response.data.loadPort) {
                        dispatch(setLoadPort(response.data.loadPort));
                    }

                    if (response.data.disport) {
                        dispatch(setDisport(response.data.disport));
                    }

                    if (response.data.lay) {
                        dispatch(setLay(Utility.DateFormat.format(new Date(response.data.lay))));
                    }

                    if (response.data.can) {
                        dispatch(setCan(Utility.DateFormat.format(new Date(response.data.can))));
                    }

                    if (response.data.remarks) {
                        dispatch(setRemarks(response.data.remarks));
                    }

                    Utility.Alert.Success("Parsed successfully");
                }
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
        }).finally(() => {
            dispatch(showLoading(false));
        });
    }

    return (
        <>
            <CustomAccordion
                title="Input"
                defaultOpen={true}
                className="top-quick-input"
                open={cargoInputViewData.cargoInputExpand}
                onOpenChange={(isOpen: boolean) => {
                    dispatch(setCargoInputExpand(isOpen));
                }}
            >
                <Grid container>
                    <Grid item lg={3} xs={12}>
                        <Grid container className='cargo-input-component'>
                            <Grid xs={10} lg={9}>
                                <ContactSelector
                                    label='Account Name'
                                    selectedContactId={cargoInputViewData.cargoInput.charterer}
                                    useFocusMode={true}
                                    focusValue={cargoInputViewData.triggerFocusChartererInput}
                                    onContactIdChange={(value: string) => {
                                        dispatch(setCharterer(value));
                                        if (value && value.length > 0) {
                                            dispatch(setCharterersForInput(value));
                                            dispatch(setTriggerCargoSearch((new Date()).getTime()));
                                        }
                                    }}
                                >
                                </ContactSelector>
                            </Grid>
                            <Grid item xs={2} lg={3}>
                                <Tooltip title="Reset (Alt + R)">
                                    <Button
                                        className="clear-input-button"
                                        color="success"
                                        variant="contained"
                                        tabIndex={-1}
                                        onClick={() => {
                                            refreshInput();
                                        }}
                                    >
                                        <i className="fa fa-refresh" aria-hidden="true"></i>
                                    </Button>
                                </Tooltip>
                            </Grid>
                        </Grid>
                        <CargoTypeSelector
                            className='cargo-input-component'
                            isGroupDisabled={true}
                            onChange={(cargoType: CargoTypeModel | null) => {
                                if (cargoType) {
                                    dispatch(setCargoType((cargoType as CargoTypeModel).id));
                                }
                            }}
                            cargoTypeValue={getCargoTypeById(cargoInputViewData.cargoInput.cargoType)}
                        >
                        </CargoTypeSelector>
                        <DwtSelector
                            dwtFromValue={cargoInputViewData.cargoInput.dwtMin}
                            dwtToValue={cargoInputViewData.cargoInput.dwtMax}
                            className="cargo-input-component"
                            dwtFromLabel='Dwt Min(T)'
                            dwtToLabel='Dwt Max(T)'
                            useSizeSelector={false}
                            onDwtFromChange={(value: number) => {
                                dispatch(setDwtMin(value));
                            }}
                            onDwtToChange={(value: number) => {
                                dispatch(setDwtMax(value));
                            }}
                        ></DwtSelector>
                    </Grid>
                    <Grid item lg={3} xs={12}>
                        <PortSelector
                            onPortChange={(value: PortModel | null) => {
                                if (value) {
                                    dispatch(setLoadPort((value as PortModel).id));
                                } else {
                                    dispatch(setLoadPort(0));
                                }
                            }}
                            className="cargo-input-component"
                            portLabel='Loadport'
                            areaLabel='Area'
                            isAreaDisabled={true}
                            portValue={getPortById(cargoInputViewData.cargoInput.loadPort)}
                            isAreaRelatedToPort={true}
                        >
                        </PortSelector>
                        <PortSelector
                            onPortChange={(value: PortModel | null) => {
                                if (value) {
                                    dispatch(setDisport((value as PortModel).id));
                                } else {
                                    dispatch(setDisport(0));
                                }
                            }}
                            className="cargo-input-component"
                            portLabel='Disport'
                            areaLabel='Area'
                            isAreaDisabled={true}
                            portValue={getPortById(cargoInputViewData.cargoInput.disport)}
                            isAreaRelatedToPort={true}
                        >
                        </PortSelector>
                        <StartEndDatepicker
                            startDateLabel="Lay"
                            endDateLabel="Can"
                            className='cargo-input-component'
                            useQuickEndDateInput={true}
                            onStartDateChange={(date: string) => {
                                if (date) {
                                    dispatch(setLay(date));
                                }
                            }}
                            onEndDateChange={(date: string) => {
                                if (date) {
                                    dispatch(setCan(date));
                                }
                            }}
                            startDateValue={cargoInputViewData.cargoInput.lay.toUpperCase()}
                            endDateValue={cargoInputViewData.cargoInput.can.toUpperCase()}
                        >
                        </StartEndDatepicker>
                    </Grid>
                    <Grid item lg={5} xs={12}
                        className="cargo-input-component"
                    >
                        <Grid container>
                            <Grid lg={3} xs={3}>
                                <TextField
                                    label="Remarks"
                                    multiline
                                    fullWidth
                                    rows={5}
                                    value={cargoInputViewData.cargoInput.remarks}
                                    onChange={(event) => {
                                        dispatch(setRemarks(event.target.value));
                                    }}
                                    className="tonnage-input-full-tc"
                                />
                            </Grid>
                            <Grid lg={9} xs={9}>
                                <TextField
                                    label="Full Order"
                                    multiline
                                    fullWidth
                                    rows={3}
                                    value={cargoInputViewData.cargoInput.orderDetail}
                                    onChange={(event) => {
                                        dispatch(setOrderDetail(event.target.value));
                                    }}
                                    className="tonnage-input-full-tc"
                                />
                                <Grid container>
                                    <Grid item xs={12}>
                                        <Button
                                            onClick={() => {
                                                parseFullOrder();
                                            }}
                                            variant="outlined"
                                        >
                                            Parse Full Order
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item lg={1} xs={12}
                    >
                        <Tooltip title="Alt + S">
                            <Button
                                onClick={() => {
                                    save(false);
                                }}
                                variant="outlined"
                            >
                                Save (s)
                            </Button>
                        </Tooltip>
                        <Button
                            onClick={() => {
                                save(true);
                            }}
                            variant="outlined"
                            className='cargo-save-and-m1'
                        >
                            Save And M1
                        </Button>
                        <FormControlLabel control={
                            <Checkbox
                                checked={cargoInputViewData.cargoInput.fixed}
                                onChange={(event) => {
                                    if (event.currentTarget.checked) {
                                        Swal.fire({
                                            title: 'This cargo will be remarked as FIXED. Do you want to continue?',
                                            icon: 'warning',
                                            showCancelButton: true,
                                            confirmButtonColor: '#3085d6',
                                            cancelButtonColor: '#d33',
                                            confirmButtonText: 'Yes',
                                            cancelButtonText: 'No'
                                        }).then((result) => {
                                            if (result.isConfirmed) {
                                                dispatch(setFixed(true));
                                                dispatch(setTriggerSave(new Date().getTime()));
                                            }
                                        })
                                    } else {
                                        dispatch(setFixed(false));
                                    }
                                }}
                            />}
                            className="tonnage-input-grabs"
                            label="Fixed"
                        />
                    </Grid>
                </Grid>
            </CustomAccordion>
        </>
    )
};

export default CargoInput;